import { useEffect, useState, useCallback } from "react";
import { UserPlus, Trash2, Loader2, ShieldCheck, User, KeyRound, Check, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/store/AuthStore";

const SHEETS = [
  { id: "calc", label: "Calc" },
  { id: "assembly", label: "Assembly" },
  { id: "order", label: "Order" },
  { id: "costing", label: "Costing" },
  { id: "rate", label: "Rate" },
  { id: "rate88", label: "Rate 8.8" },
  { id: "import", label: "Import Order" },
  { id: "stock", label: "Stock List" },
  { id: "tables", label: "Tables" },
];

export function TeamDialog({ open, onOpenChange }) {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", username: "", password: "", role: "member" });
  const [saving, setSaving] = useState(false);
  const [pwEdit, setPwEdit] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/team");
      setMembers(data);
    } catch (e) {
      toast.error(apiError(e, "Could not load team"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const addMember = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/team", form);
      toast.success(`${form.name} added`);
      setForm({ name: "", username: "", password: "", role: "member" });
      load();
    } catch (err) {
      toast.error(apiError(err, "Could not add member"));
    } finally {
      setSaving(false);
    }
  };

  const removeMember = async (m) => {
    try {
      await api.delete(`/team/${m.id}`);
      toast.success(`${m.name} removed`);
      load();
    } catch (err) {
      toast.error(apiError(err, "Could not remove member"));
    }
  };

  const savePassword = async (m) => {
    const value = pwEdit[m.id]?.value || "";
    if (value.length < 4) return toast.error("Password must be at least 4 characters");
    setPwEdit((p) => ({ ...p, [m.id]: { ...p[m.id], saving: true } }));
    try {
      await api.put(`/team/${m.id}/password`, { password: value });
      toast.success(`Password updated for ${m.name}`);
      setPwEdit((p) => ({ ...p, [m.id]: undefined }));
    } catch (err) {
      toast.error(apiError(err, "Could not update password"));
      setPwEdit((p) => ({ ...p, [m.id]: { ...p[m.id], saving: false } }));
    }
  };

  const toggleSheet = async (m, sheetId) => {    const current = m.permissions || SHEETS.map((s) => s.id);
    const next = current.includes(sheetId) ? current.filter((s) => s !== sheetId) : [...current, sheetId];
    setMembers((prev) => prev.map((x) => (x.id === m.id ? { ...x, permissions: next } : x)));
    try {
      await api.put(`/team/${m.id}/permissions`, { permissions: next });
    } catch (err) {
      toast.error(apiError(err, "Could not update access"));
      load();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-slate-700 bg-[#131C2E] text-slate-100 sm:max-w-lg" data-testid="team-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <ShieldCheck className="h-5 w-5 text-orange-400" /> Manage Team
          </DialogTitle>
          <DialogDescription className="text-slate-400">Add or remove team members who can log in.</DialogDescription>
        </DialogHeader>

        <form onSubmit={addMember} className="grid grid-cols-2 gap-2 rounded-xl border border-slate-800 bg-slate-900/50 p-3">
          <Input data-testid="team-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Name" required className="h-10 rounded-lg border-slate-700 bg-slate-950 text-white" />
          <Input data-testid="team-username" autoCapitalize="none" value={form.username} onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))} placeholder="Username" required className="h-10 rounded-lg border-slate-700 bg-slate-950 text-white" />
          <Input data-testid="team-password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} placeholder="Temp password" required className="h-10 rounded-lg border-slate-700 bg-slate-950 text-white" />
          <Button type="submit" disabled={saving} data-testid="team-add-btn" className="h-10 rounded-lg bg-orange-500 font-bold text-slate-950 hover:bg-orange-400">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UserPlus className="mr-1 h-4 w-4" /> Add</>}
          </Button>
        </form>

        <div className="max-h-64 space-y-2 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-slate-500" /></div>
          ) : (
            members.map((m) => (
              <div key={m.id} data-testid={`team-member-${m.id}`} className="rounded-xl border border-slate-800 bg-slate-900/50 px-3 py-2.5">
                <div className="flex items-center gap-3">
                  <div className={`flex h-8 w-8 items-center justify-center rounded-full ${m.role === "admin" ? "bg-orange-500/20 text-orange-300" : "bg-slate-700/50 text-slate-300"}`}>
                    {m.role === "admin" ? <ShieldCheck className="h-4 w-4" /> : <User className="h-4 w-4" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-slate-100">{m.name} {m.id === user?.id && <span className="text-xs text-slate-500">(you)</span>}</div>
                    <div className="truncate text-[11px] text-slate-500">@{m.username} · {m.role}</div>
                  </div>
                  {m.id !== user?.id && (
                    <button onClick={() => removeMember(m)} data-testid={`team-remove-${m.id}`} className="rounded-lg p-2 text-slate-500 hover:bg-red-500/10 hover:text-red-400">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <div className="mt-2 border-t border-slate-800 pt-2">
                  {pwEdit[m.id] ? (
                    <div className="flex items-center gap-1.5">
                      <Input
                        data-testid={`team-password-input-${m.id}`}
                        autoFocus
                        type="text"
                        value={pwEdit[m.id].value || ""}
                        onChange={(e) => setPwEdit((p) => ({ ...p, [m.id]: { ...p[m.id], value: e.target.value } }))}
                        placeholder="New password"
                        className="h-9 flex-1 rounded-lg border-slate-700 bg-slate-950 text-white"
                      />
                      <Button size="icon" disabled={pwEdit[m.id].saving} onClick={() => savePassword(m)} data-testid={`team-password-save-${m.id}`} className="h-9 w-9 rounded-lg bg-orange-500 text-slate-950 hover:bg-orange-400">
                        {pwEdit[m.id].saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                      </Button>
                      <Button size="icon" variant="outline" onClick={() => setPwEdit((p) => ({ ...p, [m.id]: undefined }))} data-testid={`team-password-cancel-${m.id}`} className="h-9 w-9 rounded-lg border-slate-700 text-slate-300">
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <button onClick={() => setPwEdit((p) => ({ ...p, [m.id]: { value: "", saving: false } }))} data-testid={`team-password-btn-${m.id}`} className="flex items-center gap-1.5 rounded-lg px-2 py-1 text-[11px] font-bold text-slate-400 hover:bg-slate-800 hover:text-orange-300">
                      <KeyRound className="h-3.5 w-3.5" /> Change Password
                    </button>
                  )}
                </div>
                {m.role !== "admin" && (
                  <div className="mt-2 border-t border-slate-800 pt-2">
                    <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">Allowed Sheets</div>
                    <div className="flex flex-wrap gap-1.5">
                      {SHEETS.map((s) => {
                        const on = (m.permissions || SHEETS.map((x) => x.id)).includes(s.id);
                        return (
                          <button
                            key={s.id}
                            data-testid={`team-perm-${m.id}-${s.id}`}
                            onClick={() => toggleSheet(m, s.id)}
                            className={`rounded-lg px-2.5 py-1 text-[11px] font-bold transition-colors ${on ? "bg-orange-500 text-slate-950" : "bg-slate-800 text-slate-500 hover:bg-slate-700"}`}
                          >
                            {s.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
